import React, { useEffect, useState } from 'react';
import './Servicios.css';
import { Link as Anchor } from 'react-router-dom';
import baseURL from '../url';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleDoubleRight, faBookOpen, faGraduationCap } from '@fortawesome/free-solid-svg-icons';
import moneda from '../moneda';
import SwiperCore from 'swiper/core';
import { Navigation, Pagination, Autoplay } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/swiper-bundle.css';
import ServiciosLoading from '../ServiciosLoading/ServiciosLoading';
import { useLocalStorage } from '../../Context/LocalStorageContext';
import FilterByLocation from '../FilterByLocation/FilterByLocation';

export default function Servicios() {
    const [servicios, setServicios] = useState([]);
    const [categorias, setCategorias] = useState([]);
    const [subcategorias, setSubcategorias] = useState([]);
    const [tipoSeleccionado, setTipoSeleccionado] = useState(null);
    const [categoriaSeleccionada, setCategoriaSeleccionada] = useState(null);
    const [loading, setLoading] = useState(true);
    const [ciudadLS] = useLocalStorage('ciudad');
    const [activeTab, setActiveTab] = useState('programas');
    const [talleres, setTalleres] = useState([]);
    const [categoriasTalleres, setCategoriasTalleres] = useState([]);
    
    SwiperCore.use([Navigation, Pagination, Autoplay]);

    useEffect(() => {
        cargarCategoria();
        cargarSubcategorias();
    }, []);

    useEffect(() => {
        if (activeTab === 'programas') {
            cargarServicios();
        } else {
            cargarTalleres();
        }
    }, [ciudadLS, activeTab]);

    useEffect(() => {
        // Seleccionar el primer tipo de servicio por defecto al cargar servicios
        if (servicios.length > 0 && !tipoSeleccionado) {
            const tipos = [...new Set(servicios.map(servicio => servicio.tipo))];
            setTipoSeleccionado(tipos[0]);
        }
    }, [servicios]);

    const cargarServicios = () => {
        fetch(`${baseURL}/serviciosGet.php`, { method: 'GET' })
            .then(response => response.json())
            .then(data => {
                if (ciudadLS) {
                    data.servicios = data.servicios.filter(servicio => servicio.idCiudad == ciudadLS);
                }
                setServicios(data.servicios || []);
                setLoading(false);
            })
            .catch(error => console.error('Error al cargar servicios:', error));
    };

    const cargarCategoria = () => {
        fetch(`${baseURL}/categoriasGet.php`, { method: 'GET' })
            .then(response => response.json())
            .then(data => {
                const categoriasOrdenadas = (data?.categorias || [])?.sort((a, b) => a.orden - b.orden);
                setCategorias(categoriasOrdenadas);
            })
            .catch(error => console.error('Error al cargar categorías:', error));
    };

    const cargarSubcategorias = () => {
        fetch(`${baseURL}/subCategoriaGet.php`, { method: 'GET' })
            .then(response => response.json())
            .then(data => {
                setSubcategorias(data.subcategorias || []);
            })
            .catch(error => console.error('Error al cargar subcategorías:', error));
    };

    const cargarTalleres = () => {
        setLoading(true);
        
        // Cargar talleres desde el nuevo endpoint
        fetch(`${baseURL}/talleresListadoGet.php`)
            .then(response => {
                if (!response.ok) {
                    throw new Error(`Error HTTP: ${response.status}`);
                }
                return response.json();
            })
            .then(talleres => {
                // Filtrar por ciudad si es necesario
                let talleresFiltrados = Array.isArray(talleres) ? talleres : [];
                
                if (ciudadLS) {
                    talleresFiltrados = talleresFiltrados.filter(taller => 
                        taller.idCiudad == ciudadLS
                    );
                }

                // Agrupar talleres por CESECO (idCategoria)
                const talleresPorCeseco = {};
                const categoriasMap = new Map();
                
                talleresFiltrados.forEach(taller => {
                    const cesecoId = taller.idCategoria || 'sin-ceseco';
                    const cesecoNombre = taller.nombre_ceseco;
                    
                    // Si es la primera vez que vemos este CESECO, inicializamos el array
                    if (!talleresPorCeseco[cesecoId]) {
                        talleresPorCeseco[cesecoId] = {
                            id: cesecoId,
                            nombre: cesecoNombre,
                            talleres: []
                        };
                    }
                    
                    // Agregamos el taller a su CESECO correspondiente
                    talleresPorCeseco[cesecoId].talleres.push(taller);
                    
                    // Mapeamos las categorías de población objetivo
                    if (taller.poblacionObjetivoId && !categoriasMap.has(taller.poblacionObjetivoId)) {
                        categoriasMap.set(taller.poblacionObjetivoId, true);
                    }
                });
                
                // Convertir el objeto de talleres por CESECO a un array
                const talleresAgrupados = Object.values(talleresPorCeseco);
                
                // Actualizar estados
                setTalleres(talleresAgrupados);
                
                // Actualizar categorías de población objetivo
                const categoriasUnicas = Array.from(categoriasMap.keys()).map(id => ({
                    idCategoria: id,
                    categoria: talleresFiltrados.find(t => t.poblacionObjetivoId === id)?.categoria_nombre || `Categoría ${id}`
                }));
                
                setCategoriasTalleres(categoriasUnicas);
            })
            .catch(error => {
                console.error('Error al cargar talleres:', error);
                setTalleres([]);
                setCategoriasTalleres([]);
            })
            .finally(() => {
                setLoading(false);
            });
    };

    // Obtener lista única de tipos de servicios
    const tiposDeServicios = [...new Set(servicios?.map(servicio => servicio.tipo))];

    // Filtrar servicios por tipo seleccionado
    const serviciosFiltrados = servicios?.filter(servicio => servicio.tipo === tipoSeleccionado);

    // Filtrar servicios por categoría seleccionada
    const serviciosFiltradosPorCategoria = categoriaSeleccionada
        ? serviciosFiltrados?.filter(servicio => servicio.idCategoria == categoriaSeleccionada)
        : serviciosFiltrados;
        
    // Filtrar talleres por categoría seleccionada
    const talleresFiltrados = categoriaSeleccionada
        ? talleres.filter(taller => taller.poblacionObjetivoId == categoriaSeleccionada)
        : talleres;

    return (
        <div id='Servicios'>
            {/* Barra de filtros unificada */}
            <div className="filters-card">
                <div className="filters-header">
                    <div className="filters-title">
                        <span className="filters-badge">Filtros</span>
                        <span className="filters-sub">Elige ciudad y tipo</span>
                    </div>
                    <div className="filters-location">
                        <FilterByLocation />
                    </div>
                </div>
                <div className="tabs-container">
                    <button 
                        className={`tab-btn ${activeTab === 'programas' ? 'active' : ''}`}
                        onClick={() => setActiveTab('programas')}
                    >
                        <FontAwesomeIcon icon={faBookOpen} className="tab-icon" />
                        Programas
                    </button>
                    <button 
                        className={`tab-btn ${activeTab === 'talleres' ? 'active' : ''}`}
                        onClick={() => setActiveTab('talleres')}
                    >
                        <FontAwesomeIcon icon={faGraduationCap} className="tab-icon" />
                        Talleres
                    </button>
                </div>
            </div>
            
            {loading ? (
                <ServiciosLoading />
            ) : (
                <div>
                    {activeTab === 'programas' ? (
                        <>
                            <div className="filtrosTipos">
                                {tiposDeServicios?.map(tipo => (
                                    <button
                                        key={tipo}
                                        className={`filtroBoton ${tipo === tipoSeleccionado ? 'activo' : ''}`}
                                        onClick={() => setTipoSeleccionado(tipo)}
                                    >
                                        {tipo}
                                    </button>
                                ))}
                            </div>
                            
                            {serviciosFiltradosPorCategoria?.length === 0 ? (
                                <div className="no-data-message">
                                    No hay programas disponibles en este momento.
                                </div>
                            ) : (
                                <div className="filtrosCategorias">
                                    <input
                                        type="button"
                                        className={`categoriaBoton ${!categoriaSeleccionada ? 'activeCategoria' : ''}`}
                                        onClick={() => setCategoriaSeleccionada(null)}
                                        value="Todo"
                                    />
                                    {categorias
                                        ?.filter(categoria => 
                                            serviciosFiltradosPorCategoria?.some(s => s.idCategoria === categoria.idCategoria)
                                        )
                                        ?.map(categoria => (
                                            <input
                                                key={categoria.idCategoria}
                                                type="button"
                                                className={`categoriaBoton ${categoriaSeleccionada === categoria.idCategoria ? 'activeCategoria' : ''}`}
                                                onClick={() => setCategoriaSeleccionada(categoria.idCategoria)}
                                                value={categoria.categoria}
                                            />
                                        ))}
                                </div>
                            )}
                            
                            {categorias
                                ?.filter(categoria => 
                                    !categoriaSeleccionada || categoria.idCategoria == categoriaSeleccionada
                                )
                                ?.map(categoria => (
                                    <div key={categoria.idCategoria} className="categoriaSection">
                                        {!categoriaSeleccionada && (
                                            <h2>
                                                {categoria.categoria} <FontAwesomeIcon icon={faAngleDoubleRight} className="iconCardh2" />
                                            </h2>
                                        )}
                                        
                                        <Swiper
                                            effect={'coverflow'}
                                            grabCursor={true}
                                            slidesPerView={'auto'}
                                            id="cardsServicio"
                                            autoplay={{ delay: 3000 }}
                                        >
                                            {serviciosFiltradosPorCategoria
                                                ?.filter(servicio => servicio.idCategoria == categoria.idCategoria)
                                                ?.map(servicio => (
                                                    <SwiperSlide key={servicio.idServicio} id="cardServicio">
                                                        <Anchor to={`/servicio/${servicio.idServicio}/${servicio.titulo.replace(/\s+/g, '-')}`}>
                                                            <div className='imgCard'>
                                                                <img src={servicio.imagen1} alt={servicio.titulo} />
                                                            </div>
                                                            <div className="cardServicioText">
                                                                <h3>{servicio.titulo}</h3>
                                                                <span>{servicio.descripcion}</span>
                                                                <div className="deFlexCard">
                                                                    <strong>
                                                                        {servicio.precio > 0 
                                                                            ? `${moneda} ${servicio.precio.toLocaleString()}`
                                                                            : 'Gratis'}
                                                                    </strong>
                                                                    <FontAwesomeIcon icon={faAngleDoubleRight} className="iconCard" />
                                                                </div>
                                                            </div>
                                                        </Anchor>
                                                    </SwiperSlide>
                                                ))}
                                        </Swiper>
                                    </div>
                                ))}
                        </>
                    ) : (
                        // Contenido de Talleres
                        <>
                            <h1>Nuestros Talleres</h1>
                            <div className="filtrosCategorias">
                                <input
                                    type="button"
                                    className={`categoriaBoton ${!categoriaSeleccionada ? 'activeCategoria' : ''}`}
                                    onClick={() => setCategoriaSeleccionada(null)}
                                    value="Todo"
                                />
                                {/* Mostrar categorías de talleres */}
                                {categoriasTalleres && categoriasTalleres.length > 0 ? (
                                    categoriasTalleres.map(categoria => (
                                        <input
                                            key={categoria.idCategoria}
                                            type="button"
                                            value={categoria.categoria}
                                            className={`categoriaBoton ${categoriaSeleccionada == categoria.idCategoria ? 'activeCategoria' : ''}`}
                                            onClick={() => setCategoriaSeleccionada(categoria.idCategoria)}
                                        />
                                    ))
                                ) : null}
                            </div>
                            
                            {talleres.length === 0 ? (
                                <div className="no-data-message">
                                    No hay talleres disponibles en este momento.
                                </div>
                            ) : (
                                <div className="ceseco-sections">
                                    {talleres.map(ceseco => {
                                        // Filtrar talleres por categoría seleccionada si existe
                                        const talleresFiltrados = categoriaSeleccionada
                                            ? ceseco.talleres.filter(taller => 
                                                taller.poblacionObjetivoId == categoriaSeleccionada
                                            )
                                            : ceseco.talleres;

                                        // No mostrar la sección si no hay talleres que coincidan con el filtro
                                        if (talleresFiltrados.length === 0) return null;

                                        return (
                                            <div key={`ceseco-${ceseco.id}`} className="ceseco-section">
                                                <h2>
                                                    {ceseco.nombre} <FontAwesomeIcon icon={faAngleDoubleRight} className="iconCardh2" />
                                                </h2>
                                                <Swiper
                                                    effect={'coverflow'}
                                                    grabCursor={true}
                                                    slidesPerView={'auto'}
                                                    className="talleres-swiper"
                                                    autoplay={{ delay: 3000 }}
                                                >
                                                    {talleresFiltrados.map(taller => (
                                                        <SwiperSlide key={taller.idTaller} id="cardServicio">
                                                            <Anchor to={`/taller/${taller.idTaller}`}>
                                                                <div className='imgCard'>
                                                                    <img src={taller.imagen1} alt={taller.nombre} />
                                                                </div>
                                                                <div className="cardServicioText">
                                                                    <h3>{taller.nombre}</h3>
                                                                    <span>{taller.descripcion || ' '}</span>
                                                                    <div className="deFlexCard">
                                                                        <strong></strong>
                                                                        <FontAwesomeIcon icon={faAngleDoubleRight} className="iconCard" />
                                                                    </div>
                                                                </div>
                                                            </Anchor>
                                                        </SwiperSlide>
                                                    ))}
                                                </Swiper>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </>
                    )}
                </div>
            )}
        </div>
    );
};
